// This file contains the sagas used for async actions in our app. It's divided into
// "effects" that the sagas call (`authorize` and `logout`) and the actual sagas themselves,
// which listen for actions.

// Sagas help us gather all our side effects (network requests in this case) in one place

import { browserHistory } from 'react-router'
import { delay } from 'redux-saga'
import { take, call, put, fork, race } from 'redux-saga/effects'
import * as auth from 'api/auth'
import * as actionNotification from 'actions/notifications'
import { getProfile as apiGetProfile } from 'sagas/api/profile'
import { change } from 'redux-form'
import * as action from 'actions'
import {
  LOGIN_REQUEST,
  REGISTER_REQUEST,
  LOGOUT,
  RESEND_VERIFICATION_EMAIL,
  BUSINESS_TYPES_REQUEST,
  REGISTER_BUSINESS_REQUEST,
  REDIRECT_TO,
  LOAD_RESET_PASSWORD,
  SEND_RESET_PASSWORD,
  LOAD_PREPARE_ENCRYPTION,
  GET_PROFILE_REQUEST,
  TO_LOGIN_PAGE,
  TO_SIGNUP_PAGE
} from 'actions/constants'
import store from 'store'
import {getProfile} from 'api/profile'
import {isSubscriptionExpired, showSubscriptionExpiredModal} from 'actions/subscriptionExpired'
import moment from 'moment'
import * as mpRegister from 'mixpanel/register'
import * as mpLogin from 'mixpanel/login'
import * as mpLogout from 'mixpanel/logout'
import _filter from 'lodash/filter'
import _orderBy from 'lodash/orderBy'

/**
 * Effect to handle authorization
 * @param  {string} email               The email of the user
 * @param  {string} password               The password of the user
 * @param  {object} options                Options
 * @param  {boolean} options.isRegistering Is this a register request?
 */
export function * authorize (email, password) {
  // We send an action that tells Redux we're sending a request
  // We then try to register or log in the user, depending on the request
  try {
    let response = yield call(auth.login, email, password)
    return response.data
  } catch (error) {
    // If we get an error we send Redux the appropiate action and return
    if (error.response) {
      const data = error.response.data
      if (data.error.user_message) {
        return {error: data.error.user_message}
      } else {
        return {error: error.message}
      }
    } else {
      return {error: error.message}
    }
  }
}

/**
 * Log in saga
 */
export function * loginFlow () {
  // Because sagas are generators, doing `while (true)` doesn't block our program
  // Basically here we say "this saga is always listening for actions"
  while (true) {
    // And we're listening for `LOGIN_REQUEST` actions and destructuring its payload
    let request = yield take(LOGIN_REQUEST)
    let {email, password} = request.payload.data
    const {next} = request.payload
    yield put(action.sendingRequest(true))
    yield put(action.showProgressBar())
    // A `LOGOUT` action may happen while the `authorize` effect is going on, which may
    // lead to a race condition. This is unlikely, but just in case, we call `race` which
    // returns the "winner", i.e. the one that finished first
    let winner = yield race({
      auth: call(authorize, email, password),
      logout: take(LOGOUT)
    })

    yield put(action.sendingRequest(false))
    yield put(action.hideProgressBar())
    if (winner.auth) {
      let {errorTypes, user, error, permissions, role} = winner.auth
      if (user) {
        // user is logged in
        let profile = yield call(getProfile)
        mpLogin.identifyFromProfile({profile: profile.data})
        mpLogin.login({profile: profile.data, role})
        yield put(action.setPermissionsState(permissions))
        yield put(action.setAuthState(true, profile.data))
        if (user.is_owner || user.is_manager) {
          if (moment(profile.data.business.expired_at) <= moment()) {
            yield put(isSubscriptionExpired(true))
            yield put(showSubscriptionExpiredModal())
            forwardTo('/settings/billing')
          } else {
            // owner or manager go to dashboard page,
            // unless next url is provided
            // if next url is a forbidden page
            // the interceptor should be given in react-router, not here
            if (next) {
              forwardTo(next)
            } else {
              // backend should return at least one, which is account setting
              let backofficePermissions = _filter(permissions, {'access': 'Back-office'})
              let firstSubMenu = _orderBy(backofficePermissions, 'priority_rank')[0].urls[0]
              forwardTo(firstSubMenu)
            }
            yield put(actionNotification.success(`Welcome back, ${user.first_name}`))
          }
        } else {
          // staff go to account settings
          let backofficePermissions = _filter(permissions, {'access': 'Back-office'})
          let firstSubMenu = _orderBy(backofficePermissions, 'priority_rank')[0].urls[0]
          forwardTo(firstSubMenu)
          yield put(actionNotification.success(`Welcome back, ${user.first_name}`))
        }
      } else if (error) {
        // user can't loging because have wrong password / email
        yield call(delay, 1000)
        yield put(actionNotification.error(error))
      } else if (errorTypes) {
        // user has logined
        setTimeout(function () {
          window.location.reload()
        }, 1000)
      }
    } else if (winner.logout) {
      yield call(logout)
      forwardTo('/login')
    }
  }
}

/**
 * Effect to handle logging out
 */
export function * logout () {
  // We tell Redux we're in the middle of a request
  yield put(action.sendingRequest(true))
  yield put(action.showProgressBar())

  // Similar to above, we try to log out by calling the `logout` function in the
  // `auth` module. If we get an error, we send an appropiate action. If we don't,
  // we return the response.
  try {
    store.remove(`response:login`)
    let response = yield call(auth.logout)
    return response
  } catch (error) {
    let eobj = new Error(error.message)
    yield put(action.requestError(eobj))
  } finally {
    yield put(action.sendingRequest(false))
    yield put(action.hideProgressBar())
  }
}

export function * resendVerificationEmailFlow () {
  while (true) {
    let request = yield take(RESEND_VERIFICATION_EMAIL)
    let {email} = request.payload

    // UI
    yield put(action.sendingRequest(true))
    yield put(action.showProgressBar())

    try {
      // api call
      const response = yield call(auth.resendVerificationEmail, email)
      yield put(action.setResendEmailVerificationState({isModalShow: true, headerMessage: 'Notification', bodyMessage: response.data.message, modalStatus: response.data.status}))
    } catch (error) { // catch exception from resendVerificationEmail call
      yield put(action.setResendEmailVerificationState({isModalShow: true, headerMessage: 'Error', bodyMessage: error.message, modalStatus: error.status}))
    } finally {
      // UI
      yield put(action.hideProgressBar())
      yield put(action.sendingRequest(false))
    }
  }
}

/**
 * Log out saga
 * This is basically the same as the `if (winner.logout)` of above, just written
 * as a saga that is always listening to `LOGOUT` actions
 */
export function * logoutFlow () {
  while (true) {
    yield take(LOGOUT)
    mpLogout.logout()
    yield call(logout)
    mpLogout.resetAfterLogout()
    // don't remove window.location.replace, because this code is temporary fix.
    window.location.replace('/login')
  }
}

/**
 * Register saga
 */
export function * registerFlow () {
  while (true) {
    let request = yield take(REGISTER_REQUEST)
    let {email, password} = request.payload.data
    mpRegister.registrationBusinessInfo({email})

    yield put(action.sendingRequest(true))
    yield put(action.showProgressBar())

    let response = yield call(auth.registered, email)
    let result = response.data
    try {
      if (result.present) {
        yield put(actionNotification.error('Email already exist!'))
        yield put(change('registerForm', 'email', null))
      } else {
        yield put(action.changeFormRegister({email: email, password: password, confpassword: password}))
        yield put(action.setIsRegistered(true))
      }
    } catch (error) {
      yield put(action.requestError(error))
    } finally {
      yield put(action.hideProgressBar())
      yield put(action.sendingRequest(false))
    }
  }
}

export function * fetchBusinessTypes () {
  yield put(action.sendingRequest(true))
  try {
    let response = yield call(auth.getBusinessTypes)
    return response.data
  } catch (error) {
    yield put(action.requestError(error))
  } finally {
    yield put(action.sendingRequest(false))
  }
}

export function * businessTypesFlow () {
  while (true) {
    yield take(BUSINESS_TYPES_REQUEST)

    let response = yield call(fetchBusinessTypes)
    yield put(action.changeBusinessTypesData(response))
  }
}

export function * fetchBusinessCategories (businessTypeId) {
  yield put(action.sendingRequest(true))
  try {
    let response = yield call(auth.getBusinessCategories, businessTypeId)
    return response.data
  } catch (error) {
    yield put(action.requestError(error))
  } finally {
    yield put(action.sendingRequest(false))
  }
}

export function * businessCategoriesFlow () {
  while (true) {
    const request = yield take('BUSINESS_CATEGORIES_REQUEST')
    const businessTypeId = request.payload.data
    const response = yield call(fetchBusinessCategories, businessTypeId)
    yield put(action.changeBusinessCategoriesData(response))
  }
}

export function * registerBusinessFlow () {
  while (true) {
    let request = yield take(REGISTER_BUSINESS_REQUEST)
    yield put(action.sendingRequest(true))
    yield put(action.showProgressBar())
    try {
      mpRegister.registrationCompleted({user: request.payload.data.user, business: request.payload.data.business})
      yield call(
        auth.registerBusiness,
        request.payload.data
      )
      yield put(
        action.changeFormBusiness(
          {
            businessName: '',
            businessType: '',
            businessCategory: '',
            businessAddress: '',
            businessCity: '',
            businessProvince: '',
            businessPostalCode: '',
            userFirstName: '',
            userLastName: '',
            userPhone: ''
          }
        )
      ) // Clear form
      forwardTo('/register-complete')
    } catch (error) {
      yield put(action.requestError(error))
      return false
    } finally {
      yield put(action.hideProgressBar())
      yield put(action.sendingRequest(false))
    }
  }
}
export function * redirectTo () {
  while (true) {
    let request = yield take(REDIRECT_TO)
    forwardTo('/' + request.payload.url)
  }
}

export function * loadResetPassword () {
  while (true) {
    let request = yield take(LOAD_RESET_PASSWORD)
    try {
      let response = yield call(auth.getResetPassword, {access_token: request.payload.token})
      let user = JSON.parse(response.data.user)
      yield put(action.changeFormResetPassword({tokenError: false, fullName: user.first_name + ' ' + user.last_name}))
    } catch (error) {
      if (error.status === 404) {
        yield put(action.changeFormResetPassword({tokenError: true, message: error.data.message}))
      }
    }
  }
}

export function * sendResetPassword () {
  while (true) {
    let requestPayload = yield take(SEND_RESET_PASSWORD)
    let responsePayload = requestPayload.payload.data
    responsePayload.access_token = requestPayload.payload.access_token
    yield put(action.sendingRequest(true))
    yield put(action.showProgressBar(true))
    try {
      yield call(auth.resetPassword, responsePayload)
      forwardTo('/success-reset-password')
    } catch (error) {
      yield put(action.requestError(error))
    } finally {
      yield put(action.hideProgressBar())
      yield put(action.sendingRequest(false))
    }
  }
}

export function * loadPrepareEncryption () {
  while (true) {
    yield take(LOAD_PREPARE_ENCRYPTION)
    try {
      let response = yield call(auth.loadPrepareEncryption)
      let responseData = response.data
      yield put(action.setPrepareEncryption({responseData}))
    } catch (e) {
      yield put(action.requestError(e))
    }
  }
}
export function * getProfileFlow () {
  while (true) {
    yield take(GET_PROFILE_REQUEST)
    let data = yield call(apiGetProfile, {})
    if (data) {
      yield put(action.setProfile(data))
    }
  }
}

export function * toLoginPage () {
  while (true) {
    yield take(TO_LOGIN_PAGE)
    forwardTo('/login')
  }
}

export function * toSignUpPage () {
  while (true) {
    yield take(TO_SIGNUP_PAGE)
    forwardTo('/register')
  }
}

// The root saga is what we actually send to Redux's middleware. In here we fork
// each saga so that they are all "active" and listening.
// Sagas are fired once at the start of an app and can be thought of as processes running
// in the background, watching actions dispatched to the store.
export default function * root () {
  yield fork(loginFlow)
  yield fork(logoutFlow)
  yield fork(registerFlow)
  yield fork(businessTypesFlow)
  yield fork(resendVerificationEmailFlow)
  yield fork(registerBusinessFlow)
  yield fork(businessTypesFlow)
  yield fork(businessCategoriesFlow)
  yield fork(redirectTo)
  yield fork(loadResetPassword)
  yield fork(sendResetPassword)
  yield fork(loadPrepareEncryption)
  yield fork(getProfileFlow)
  yield fork(toLoginPage)
  yield fork(toSignUpPage)
}

// Little helper function to abstract going to different pages
function forwardTo (location) {
  browserHistory.push(location)
}



// WEBPACK FOOTER //
// ./app/sagas/index.js